import {Point} from "@/model/point";
import * as Collections from 'typescript-collections';
import {Replica} from "@/store/meet/types";

export interface PlaceState {
    tiles: Tile[];
    places: Place[];
    educations: Education[];
    idsCache: Collections.Set<number>;
    dialogs: Dialog[];
    dialogIdsCache: string[];
}

export class Dialog {
    id!: string;
    bubbles!: Bubble[];
    y!: number;
    activation!: DialogActivation;
    auto!: boolean;
    loop!: boolean;
    loopPeriodMs!: number;
    onClick!: DialogOnClick;
    showing: boolean = false;
    requireMarker: string | undefined;
    forbidMarker: string | undefined;
}

export enum DialogActivation {
    Y_VISIBLE = 'Y_VISIBLE',
    Y_REACHED = 'Y_REACHED',
    INSTANT = 'INSTANT'
}

export enum DialogOnClick {
    SPEEDUP = 'SPEEDUP',
    PAUSE_RESTART = 'PAUSE_RESTART',
    PAUSE_CONTINUE = 'PAUSE_CONTINUE',
    CANCEL = 'CANCEL',
    IGNORE = 'IGNORE',
    IGNORE_IF_SHOWING = 'IGNORE_IF_SHOWING',
    ANSWER_ON_TOP = 'ANSWER_ON_TOP'
}

export class Bubble {
    delayMs!: number;
    text!: string;
    x!: number;
    y!: number;
    giveMarker: string | undefined;
}

export class Tile {
    id!: number;
    imageId!: string;
    yfrom!: number;
    yto!: number;
    textureLength!: number;
}

export class SpawnDetails {
    appearAnim!: AppearAnim | undefined;
    appearFromX: number | undefined;
    appearFromY: number | undefined;
    spawnDelay: number | undefined;
    spawnSound: string | undefined;
    spawnSoundDelay: number | undefined;
    duration: number | undefined;
}

export class Place {
    id!: number;
    placeType!: PlaceType;
    posX!: number;
    posY!: number;
    creatureId!: string | null;
    creatureLevel!: number | null;
    creatureNamePlural!: string | null;
    special!: boolean;
    battleLost!: boolean;
    positionType!: PositionType;
    mandatory!: boolean;
    dropShadow!: boolean;

    educations!: Education[];

    talks!: Talk[];
    talksOnClick!: OnClickType;
    talksLoop!: boolean;
    loopPeriod: number | undefined;

    depth!: number;

    dialogId: string | undefined;

    spawnDetails: SpawnDetails | undefined;

    accept(visitor: PlaceVisitor): void {}

    getPosition(): Point {
        return new Point(this.posX, this.posY);
    }
}

export enum AppearAnim {
    TWO_PEAKS_FADE = 'TWO_PEAKS_FADE',
    FADE = 'FADE',
    MEDIUM_FADE = 'MEDIUM_FADE',
    SLOW_FADE = 'SLOW_FADE',
    FADE_FLY_UP = 'FADE_FLY_UP',
    FADE_FLY_UP_FAST = 'FADE_FLY_UP_FAST',
    FLY_FROM = 'FLY_FROM',
    FLY_FROM_2 = 'FLY_FROM_2',
    FALL_DOWN = 'FALL_DOWN',
    TWO_PEAKS_FADE_SLOW = 'TWO_PEAKS_FADE_SLOW',
    THREE_FLASHES = 'THREE_FLASHES',
    ONE_FLASH = 'ONE_FLASH',
    SLIDE_FLASH = 'SLIDE_FLASH'
}


export enum PlaceType {
    RESOURCE = 'RESOURCE',
    CUSTOM = 'CUSTOM_PLACE',
    ARTIFACT = 'ARTIFACT',
    RUNE = 'RUNE',
    CONSUMABLE = 'CONSUMABLE',
    POINTER = 'POINTER',
    TURN = 'TURN',
    GAINABLE = 'GAINABLE',
    SCRIPT = 'SCRIPT',
    PORTAL = 'PORTAL',
    SKILL = 'SKILL',
    TALKER = 'TALKER',
    NATURE = 'NATURE'
}

export enum ResourceType {
    GOLD = 'GOLD',
    CRYSTAL = 'CRYSTAL',
    ELIXIR = 'ELIXIR',
    MITHRIL = 'MITHRIL',
    SULFUR = 'SULFUR',
    CAVIAR = 'CAVIAR',
    TICKETS = 'TICKETS',
    EVENT_CURRENCY = 'EVENT_CURRENCY',
    ORE_GOLD = 'ORE_GOLD',
}

export enum SkillType {
    ATTACK = 'ATTACK',
    DEFENCE = 'DEFENCE',
    HEALTH = 'HEALTH',
    IMMORTALITY = 'IMMORTALITY',
    POISON = 'POISON'
}

export enum PositionType {
    SMALL = 'SMALL',
    BIG = 'BIG',
    WATER_SMALL = 'WATER_SMALL',
    WATER_BIG = 'WATER_BIG',
}

export enum PlaceDisplayType {
    PANEL = 'PANEL',
    DIALOG = 'DIALOG'
}

export enum AnimType {
    NONE = 'NONE',
    TEETER = 'TEETER',
    FLYING = 'FLYING',
    ARROW = 'ARROW',
    FLY_VERTICAL = 'FLY_VERTICAL',
    CAT_DROWN_MALE = 'CAT_DROWN_MALE',
    CAT_DROWN_FEMALE = 'CAT_DROWN_FEMALE',
    EMPTY = 'EMPTY',
    SHRINK_FADE = 'SHRINK_FADE',
    FLY_FADE = 'FLY_FADE',
    FADE_IN_OUT = 'FADE_IN_OUT'
}

export class Talk {
    delayMs!: number;
    text!: string;
    giveMarker!: string;
}

export enum OnClickType {
    STOP = 'STOP',
    IGNORE = 'IGNORE'
}

export class Education {
    id!: string;
    triggerType!: TriggerType;
    distanceFromTopOfScreen!: number;
    delayMs!: number;
    eduBlock: EduBlock | undefined;
    heroComment: HeroComment | undefined;
    scriptId: string | undefined;
    postScriptId: string | undefined;
    giveMarker: string | undefined;
    removeMarker: string | undefined;
    arrow!: boolean;
    arrowX!: number;
    arrowY!: number;
    arrowMode!: ArrowMode;
    roadHand!: boolean;
    stopMovement!: boolean;
    forbidMovement!: boolean;
    requiredMarkers!: string[];
    forbiddenMarkers!: string[];
    onlyIfGuarded!: boolean;
}

export class HeroComment {
    heroId!: number;
    text!: string;
    commentDuration!: number | undefined;
    delayMs!: number | undefined;
    mode!: HeroCommentMode;
}

export class SimpleTalk {
    delayMs: number | undefined;
    text!: string;
}


export class EduBlock {
    atlasId!: string;
    imageId!: string;
    title!: string;
    text!: string;
    answer!: string;
}

export enum ArrowMode {
    PERSISTENT = 'PERSISTENT',
    PERIODIC = 'PERIODIC',
    ONE_TIME = 'ONE_TIME'
}

export enum AutoAnswerType {
    NONE = 'NONE',
    ON_CLICK = 'ON_CLICK',
    ON_DISTANCE = 'ON_DISTANCE',
    ON_VISIBLE = 'ON_VISIBLE',
    INSTANT = 'INSTANT'
}

export enum TriggerType {
    UNKNOWN = 'UNKNOWN',
    DELAY = 'DELAY',
    DISTANCE = 'DISTANCE',
    BATTLE_WON = 'BATTLE_WON',
    BATTLE_LOST = 'BATTLE_LOST',
    COMPLETE = 'COMPLETE',
    PREMISS = 'PREMISS',
    MISS = 'MISS'
}

export class GiveItem {
    itemId!: string;
    name!: string;
    description!: string | undefined;
    atlasId!: string | undefined;
    imageId!: string | undefined;
    giveItemMode!: GiveItemMode;
}

export class TeleportDto {
    title!: string;
    emptyIntro!: string;
    emptyText!: string;
    presentText!: string;
    destinations!: DestinationDto[];
}

export class DestinationDto {
    roadId!: string;
    name!: string;
    action!: string;
}

export class ResourcePlace extends Place {
    resourceType!: ResourceType;
    count!: number;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitResourcePlace(this);
    }
}

export class AutoAnswer {
    autoAnswerType: AutoAnswerType | undefined;
    autoAnswerDistance!: number | undefined;
    autoAnswerDelayMs!: number | undefined;
}

export class CustomPlace extends Place implements AnimatedPlace {
    placeId!: string;
    atlasId!: string;
    imageId!: string;
    animationId: string | undefined;
    placeDisplayType!: PlaceDisplayType;
    lighting!: boolean;
    lightingX!: number;
    lightingY!: number;

    animType!: AnimType;
    shadowOffset!: number;
    shadowScale!: number;

    firstReplica: Replica | undefined;
    used!: boolean;
    completed!: boolean;

    autoAnswer: AutoAnswer | undefined;

    objectComment: string | undefined;
    proxyTo: string | undefined;
    music: string | undefined;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitCustomPlace(this);
    }
}

export class ArtifactPlace extends Place {
    artifactId!: string;
    atlasId!: string;
    imageId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitArtifactPlace(this);
    }
}

export class RunePlace extends Place {
    runeId!: string;
    atlasId!: string;
    imageId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitRunePlace(this);
    }
}

export class ConsumablePlace extends Place {
    consumableId!: string;
    imageId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitConsumablePlace(this);
    }
}

export class PointerPlace extends Place {
    comment!: string;
    atlasId!: string;
    imageId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitPointerPlace(this);
    }
}

export class TurnPlace extends Place {
    comment!: string;
    imageId!: string;
    lighting!: boolean;
    lightingX!: number;
    lightingY!: number;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitTurnPlace(this);
    }
}

export class NaturePlace extends Place {
    animType: AnimType | undefined;
    atlasId: string | undefined;
    imageId!: string;
    objectComment: string | undefined;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitNaturePlace(this);
    }
}

export class GainablePlace extends Place {
    placeId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitGainablePlace(this);
    }
}

export class ScriptPlace extends Place {
    identifier!: string;
    used!: boolean;
    name!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitScriptPlace(this);
    }
}

export class PortalPlace extends Place {
    portalId!: string;
    atlasId!: string;
    imageId!: string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitPortalPlace(this);
    }
}

export class TalkerPlace extends Place implements AnimatedPlace {
    talkerId!: string;
    atlasId!: string;
    imageId!: string;
    phrases!: string[];
    autoPlay!: boolean;
    inOrder!: boolean;
    initialDelay!: number;
    intervalFrom!: number;
    intervalTo!: number;

    animationId: string | undefined;
    animType!: AnimType;
    shadowOffset!: number;
    shadowScale!: number;
    objectComment: string | undefined;
    display!: TalkDisplayType;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitTalkerPlace(this);
    }
}

export class SkillPlace extends Place {
    skillType!: SkillType;
    count!: number;
    customAtlas?:string;
    customImage?:string;

    public accept(visitor: PlaceVisitor): void {
        visitor.visitSkillPlace(this);
    }
}


export interface PlaceVisitor {
    visitSkillPlace(place: SkillPlace): void;
    visitResourcePlace(place: ResourcePlace): void;
    visitCustomPlace(place: CustomPlace): void;
    visitArtifactPlace(place: ArtifactPlace): void;
    visitRunePlace(place: RunePlace): void;
    visitConsumablePlace(place: ConsumablePlace): void;
    visitPointerPlace(place: PointerPlace): void;
    visitTurnPlace(place: TurnPlace): void;
    visitGainablePlace(place: GainablePlace): void;
    visitScriptPlace(place: ScriptPlace): void;
    visitPortalPlace(place: PortalPlace): void;
    visitTalkerPlace(place: TalkerPlace): void;
    visitNaturePlace(place: NaturePlace): void;
}

export interface AnimatedPlace {
    atlasId: string | undefined;
    imageId: string;
    animationId: string | undefined;
    animType: AnimType;
    shadowOffset: number;
    shadowScale: number;
}

export enum TalkDisplayType {
    TALK = 'TALK',
    OBJECT_COMMENT = 'OBJECT_COMMENT'
}

export enum GiveItemMode {
    HOLIDAY = 'HOLIDAY',
    USUAL = 'USUAL'
}

export enum HeroCommentMode {
    BOX = 'BOX',
    TALK = 'TALK'
}
